import { computed } from "vue";
import { useQuery, type QueryKey } from "@tanstack/vue-query";
import { getSidenavItems, getSidenavSections } from "@/services/sidenavService";
import {
  getClientSidenavItems,
  type ClientSidenavAssignment,
} from "@/services/clientSidenavService";
import {
  getInternalUserSidenavItems,
  type InternalUserSidenavAssignment,
} from "@/services/internalUserSidenavService";
import { shouldUseClientDefaultFallback } from "@/utils/accessControl";
import type { SidenavItem, SidenavSection } from "@/services/sidenavService";
import type {
  AuthAccessContext,
  AuthAccessSidebarItem,
  AuthAccessSidebarSection,
} from "@/types/authAccess";

export interface SideNavigationQueryResult {
  sections: SidenavSection[];
  items: SidenavItem[];
  hasExplicitSidenavAssignments: boolean;
}

export interface SideNavigationAuthLike {
  accountType: "internal_user" | "client" | null;
  userType: "user" | "client" | "";
  clientSidenavUserId: string | number | null;
  userLevel: string;
  roleAccess?: string;
  isSuperAdmin: boolean;
  isTaskOnlyInternalUser: boolean;
  accessContext: AuthAccessContext | null;
}

// const CLIENT_VISIBLE_ROUTE_PATHS = new Set([
//   "/overview",
//   "/client/dashboard-v2",
//   "/onboarding",
//   "/updates",
//   "/apps/task-dashboard",
//   "/communication/chat",
//   "/leads",
//   "/reports",
// ]);
const MEMBER_USER_LEVELS = new Set(["3", "4", "5", "7", "8"]);
const MEMBER_DEFAULT_ROUTE_PATHS = [
  "/overview",
  "/client/dashboard-v2",
  "/updates",
  "/apps/task-dashboard",
  "/onboarding",
  "/communication/chat",
  "/reports",
  "/reports-v2",
  "/reports-v2/seo-reports",
  "/clients",
];
const ADMIN_DEFAULT_ROUTE_PATHS = [
  "/overview",
  "/client/dashboard-v2",
  "/updates",
  "/apps/task-dashboard",
  "/onboarding",
  "/communication/chat",
  "/leads",
  "/clients",
  "/reports-v2",
  "/reports-v2/seo-reports",
];
const CLIENT_DEFAULT_ROUTE_PATHS = [
  "/overview",
  "/client/dashboard-v2",
  "/onboarding",
  "/updates",
  "/apps/task-dashboard",
  "/communication/chat",
  "/leads",
  "/reports",
];
export const normalizeSidebarRoutePath = (
  item: Pick<SidenavItem, "route" | "iframe_url"> | null | undefined
) => {
  if (!item || typeof item.route !== "string") return "";

  if (item.iframe_url && item.route) {
    const rawRoute = String(item.route).trim();
    if (!rawRoute) return "";

    if (rawRoute.startsWith("/")) {
      return rawRoute;
    }

    const category = String(
      (item as SidenavItem & { category?: string })?.category || "apps"
    )
      .trim()
      .replace(/^\/+|\/+$/g, "");
    const normalizedRoute = rawRoute.replace(/^\/+/, "");

    if (!category) {
      return `/${normalizedRoute}`;
    }

    return `/${category}/${normalizedRoute}`;
  }

  return item.route.startsWith("/") ? item.route : `/${item.route}`;
};

const extractAssignedIds = (
  assignments: Array<ClientSidenavAssignment | InternalUserSidenavAssignment>
) =>
  assignments.flatMap((assignment) => {
    const itemId = assignment?.sidenav_item?.id;
    return itemId ? [itemId] : [];
  });

const mapAuthSidebarItemToSidenavItem = (
  item: AuthAccessSidebarItem
): SidenavItem => ({
  ...item,
  created_by: null,
  updated_by: null,
  created_at: "",
  updated_at: "",
  section: null,
});

const mapAuthSidebarSectionToSidenavSection = (
  section: AuthAccessSidebarSection
): SidenavSection => ({
  ...section,
  items: (section.items || []).map(mapAuthSidebarItemToSidenavItem),
});

const filterSectionsByAllowedRoutes = (
  sections: SidenavSection[],
  allowedRoutes: string[]
) => {
  const allowed = new Set(allowedRoutes);

  return (sections || [])
    .map((section) => ({
      ...section,
      items: (section.items || []).filter(
        (item) => item.is_active && allowed.has(normalizeSidebarRoutePath(item))
      ),
    }))
    .filter((section) => section.items && section.items.length > 0);
};

const renameClientTaskItem = (item: SidenavItem): SidenavItem => {
  if (normalizeSidebarRoutePath(item) !== "/apps/task-dashboard") {
    return item;
  }

  return {
    ...item,
    label: "Work Progress",
  };
};

const renameClientTaskItemsInSections = (sections: SidenavSection[]) =>
  (sections || []).map((section) => ({
    ...section,
    items: (section.items || []).map(renameClientTaskItem),
  }));

const normalizeRoleAccessValue = (value: unknown): string =>
  String(value ?? "").trim();

const isMemberInternalUser = (auth: SideNavigationAuthLike) => {
  if (normalizeRoleAccessValue(auth.roleAccess) === "3") {
    return auth.userType === "user";
  }

  return (
    auth.userType === "user" && MEMBER_USER_LEVELS.has(String(auth.userLevel))
  );
};

const isRestrictedAdminInternalUser = (auth: SideNavigationAuthLike) => {
  if (normalizeRoleAccessValue(auth.roleAccess) === "2") {
    return auth.userType === "user" && !auth.isSuperAdmin;
  }

  return (
    auth.userType === "user" &&
    !auth.isSuperAdmin &&
    ["1", "2"].includes(String(auth.userLevel))
  );
};

export const getSideNavigationQueryKey = (
  auth: SideNavigationAuthLike
): QueryKey => {
  return [
    "sidenav",
    auth.accountType || "",
    auth.userType || "",
    auth.clientSidenavUserId == null ? "" : String(auth.clientSidenavUserId),
    auth.userLevel || "",
    auth.roleAccess || "",
    (auth.accessContext?.sidebar?.allowed_route_paths || []).join("|"),
    Boolean(auth.accessContext),
  ];
};

export const resolveSideNavigationQuery = async (
  auth: SideNavigationAuthLike
): Promise<SideNavigationQueryResult> => {
  const useClientDefaultFallback =
    auth.accountType === "client" &&
    shouldUseClientDefaultFallback(auth.accessContext);

  if (
    auth.accessContext?.sidebar &&
    !auth.isSuperAdmin &&
    !useClientDefaultFallback
  ) {
    const sections = (auth.accessContext.sidebar.sections || []).map(
      mapAuthSidebarSectionToSidenavSection
    );

    return {
      items: sections.flatMap((section) => section.items || []),
      sections,
      hasExplicitSidenavAssignments: Boolean(
        auth.accessContext.module_access?.has_explicit_assignments
      ),
    };
  }

  const ut = auth.userType;

  try {
    const sectionsResponse = await getSidenavSections();
    const itemsResponse = await getSidenavItems();
    let nextSidenavItems = itemsResponse || [];
    let nextSections = sectionsResponse || [];
    let nextHasExplicitSidenavAssignments = false;

    if (
      ut === "client" &&
      auth.clientSidenavUserId != null &&
      auth.clientSidenavUserId !== ""
    ) {
      try {
        // The reference backend reserves this endpoint for manage-access permission.
        // Ordinary clients already receive their allowed sidebar in access_context.
        const canReadAssignments = auth.accessContext?.permission_access?.effective_allowed_permission_keys
          ?.includes("clients.manage_access");
        const assignments = canReadAssignments
          ? await getClientSidenavItems(auth.clientSidenavUserId)
          : [];
        nextHasExplicitSidenavAssignments = !!assignments?.length;

        if (assignments.length === 0) {
          const defaultIds = nextSidenavItems
            .filter((item) =>
              CLIENT_DEFAULT_ROUTE_PATHS.includes(
                normalizeSidebarRoutePath(item)
              )
            )
            .map((item) => item.id);

          if (defaultIds.length > 0) {
            nextSections = sectionsResponse
              .map((section) => ({
                ...section,
                items: (section.items || []).filter(
                  (item) => defaultIds.includes(item.id) && item.is_active
                ),
              }))
              .filter((section) => section.items && section.items.length > 0);
          } else {
            nextSections = filterSectionsByAllowedRoutes(
              sectionsResponse,
              CLIENT_DEFAULT_ROUTE_PATHS
            );
          }
        } else {
          const assignedIds = extractAssignedIds(assignments);

          nextSections = sectionsResponse
            .map((section) => ({
              ...section,
              items: (section.items || []).filter(
                (item) => assignedIds.includes(item.id) && item.is_active
              ),
            }))
            .filter((section) => section.items && section.items.length > 0);
        }
      } catch (err) {
        const errorResponse = err as {
          response?: {
            status?: number;
          };
        };
        const status = Number(errorResponse?.response?.status || 0);
        if (![403].includes(status)) {
          console.error("Client sidenav assignment error:", err);
        }
        nextSections = filterSectionsByAllowedRoutes(
          sectionsResponse,
          CLIENT_DEFAULT_ROUTE_PATHS
        );
      }
    } else if (
      ut === "user" &&
      auth.clientSidenavUserId != null &&
      auth.clientSidenavUserId !== ""
    ) {
      if (auth.isSuperAdmin) {
        nextHasExplicitSidenavAssignments = false;
        nextSections = sectionsResponse
          .map((section) => ({
            ...section,
            items: (section.items || []).filter((item) => item.is_active),
          }))
          .filter((section) => section.items && section.items.length > 0);

        return {
          items: nextSidenavItems,
          sections: nextSections,
          hasExplicitSidenavAssignments: nextHasExplicitSidenavAssignments,
        };
      }

      try {
        const assignments = await getInternalUserSidenavItems(
          auth.clientSidenavUserId
        );
        nextHasExplicitSidenavAssignments = !!assignments?.length;

        if (assignments.length === 0) {
          if (isMemberInternalUser(auth)) {
            nextSections = filterSectionsByAllowedRoutes(
              sectionsResponse,
              MEMBER_DEFAULT_ROUTE_PATHS
            );
          } else if (isRestrictedAdminInternalUser(auth)) {
            nextSections = filterSectionsByAllowedRoutes(
              sectionsResponse,
              ADMIN_DEFAULT_ROUTE_PATHS
            );
          } else {
            nextSections = sectionsResponse
              .map((section) => ({
                ...section,
                items: (section.items || []).filter((item) => item.is_active),
              }))
              .filter((section) => section.items && section.items.length > 0);
          }
        } else {
          const assignedIds = extractAssignedIds(assignments);

          nextSections = sectionsResponse
            .map((section) => ({
              ...section,
              items: (section.items || []).filter(
                (item) => assignedIds.includes(item.id) && item.is_active
              ),
            }))
            .filter((section) => section.items && section.items.length > 0);
        }
      } catch (err) {
        console.error("Internal user sidenav assignment error:", err);
        if (isMemberInternalUser(auth)) {
          nextSections = filterSectionsByAllowedRoutes(
            sectionsResponse,
            MEMBER_DEFAULT_ROUTE_PATHS
          );
        } else if (isRestrictedAdminInternalUser(auth)) {
          nextSections = filterSectionsByAllowedRoutes(
            sectionsResponse,
            ADMIN_DEFAULT_ROUTE_PATHS
          );
        } else {
          nextSections = sectionsResponse
            .map((section) => ({
              ...section,
              items: (section.items || []).filter((item) => item.is_active),
            }))
            .filter((section) => section.items && section.items.length > 0);
        }
      }
    } else if (ut === "client") {
      nextHasExplicitSidenavAssignments = false;
      nextSections = sectionsResponse
        .map((section) => ({
          ...section,
          items: (section.items || []).filter((item) => {
            if (!item.is_active) return false;
            return CLIENT_DEFAULT_ROUTE_PATHS.includes(
              normalizeSidebarRoutePath(item)
            );
          }),
        }))
        .filter((section) => section.items && section.items.length > 0);
    } else {
      nextHasExplicitSidenavAssignments = false;
      nextSections = sectionsResponse
        .map((section) => ({
          ...section,
          items: (section.items || []).filter((item) => item.is_active),
        }))
        .filter((section) => section.items && section.items.length > 0);
    }

    if (auth.accountType === "client" || auth.userType === "client") {
      nextSections = renameClientTaskItemsInSections(nextSections);
      nextSidenavItems = nextSidenavItems.map(renameClientTaskItem);
    }

    if (auth.accountType !== "client" && auth.userType !== "client") {
      nextSections = nextSections
        .map((section) => ({
          ...section,
          items: (section.items || []).filter(
            (item) => normalizeSidebarRoutePath(item) !== "/client/onboarding"
          ),
        }))
        .filter((section) => section.items && section.items.length > 0);

      nextSidenavItems = nextSidenavItems.filter(
        (item) => normalizeSidebarRoutePath(item) !== "/client/onboarding"
      );
    }

    return {
      items: nextSidenavItems,
      sections: nextSections,
      hasExplicitSidenavAssignments: nextHasExplicitSidenavAssignments,
    };
  } catch (error) {
    console.error("Failed to fetch sidenav:", error);
    return {
      items: [],
      sections: [],
      hasExplicitSidenavAssignments: false,
    };
  }
};

export function useSideNavigationQuery(auth: SideNavigationAuthLike) {
  return useQuery({
    queryKey: computed(() => getSideNavigationQueryKey(auth)),
    queryFn: () => resolveSideNavigationQuery(auth),
    enabled: computed(() => auth.accountType !== null && !!auth.userType),
  });
}
